import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import {
  fetchNewsDetail,
  fetchNewsQueue,
  triggerStoryline
} from '../../services/stewardNews.js';

export const StewardDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedEventId, setSelectedEventId] = React.useState<string | null>(null);
  const [selectedInstrumentId, setSelectedInstrumentId] = React.useState<string>('');

  const newsQueueQuery = useQuery({
    queryKey: ['steward', 'news', 'queue'],
    queryFn: () => fetchNewsQueue({ limit: 50 }),
  });

  const newsDetailQuery = useQuery({
    queryKey: ['steward', 'news', 'detail', selectedEventId],
    queryFn: () => (selectedEventId ? fetchNewsDetail(selectedEventId) : Promise.resolve(null)),
    enabled: Boolean(selectedEventId),
  });

  const triggerMutation = useMutation({
    mutationFn: ({ eventId, instrumentId }: { eventId: string; instrumentId: string }) =>
      triggerStoryline(eventId, instrumentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['steward', 'news', 'detail', selectedEventId] });
      setSelectedInstrumentId('');
    },
  });

  React.useEffect(() => {
    if (!selectedEventId && newsQueueQuery.data?.items?.length) {
      setSelectedEventId(newsQueueQuery.data.items[0].eventId);
    }
  }, [newsQueueQuery.data, selectedEventId]);

  const handleTrigger = () => {
    if (selectedEventId && selectedInstrumentId) {
      triggerMutation.mutate({ eventId: selectedEventId, instrumentId: selectedInstrumentId });
    }
  };

  const newsItems = newsQueueQuery.data?.items ?? [];
  const detail = newsDetailQuery.data ?? null;

  return (
    <Box
      sx={{
        flex: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Typography variant="h5">Steward Console - News Queue</Typography>

        <Stack direction="row" spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Left Pane: News Articles List */}
          <Paper
            variant="outlined"
            sx={{ width: 400, flexShrink: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                News Articles ({newsItems.length})
              </Typography>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {newsQueueQuery.isLoading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </Stack>
              ) : newsQueueQuery.isError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  Failed to load news queue
                </Alert>
              ) : newsItems.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>
                  No news articles in queue.
                </Alert>
              ) : (
                <List disablePadding>
                  {newsItems.map((item) => {
                    const selected = selectedEventId === item.eventId;
                    return (
                      <ListItemButton
                        key={item.eventId}
                        selected={selected}
                        onClick={() => setSelectedEventId(item.eventId)}
                        alignItems="flex-start"
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" noWrap>
                              {item.title}
                            </Typography>
                          }
                          secondary={
                            <Stack spacing={0.5}>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {item.source || 'Unknown source'} •{' '}
                                {new Date(item.ts).toLocaleString()}
                              </Typography>
                              {item.domain && (
                                <Chip label={item.domain} size="small" sx={{ width: 'fit-content' }} />
                              )}
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Paper>

          {/* Right Pane: News Detail */}
          <Paper variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {newsDetailQuery.isLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : !detail ? (
              <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a news article to view details.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={2} sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
                {/* Article Metadata */}
                <Stack spacing={1}>
                  <Typography variant="h6">{detail.title}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="caption" color="text.secondary">
                      {detail.source || 'Unknown source'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(detail.ts).toLocaleString()}
                    </Typography>
                    {detail.domain && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Chip label={detail.domain} size="small" />
                      </>
                    )}
                  </Stack>
                  {detail.url && (
                    <Link href={detail.url} target="_blank" rel="noopener" variant="body2">
                      View article
                    </Link>
                  )}
                  {detail.excerpt && (
                    <Typography variant="body2" color="text.secondary">
                      {detail.excerpt}
                    </Typography>
                  )}
                </Stack>

                <Divider />

                {/* Extracted Exposures Section */}
                {detail.extraction && (
                  <>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1">
                        Extracted Exposures
                        {detail.extraction.confidence !== undefined && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (confidence: {(detail.extraction.confidence * 100).toFixed(0)}%)
                          </Typography>
                        )}
                      </Typography>
                      
                      {/* Exposure Keys */}
                      {detail.extraction.keys.length > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                          {detail.extraction.keys.map((key, idx) => {
                            const [prefix, value] = key.split('#', 2);
                            const colorMap: Record<string, 'primary' | 'secondary' | 'warning' | 'info' | 'success' | 'error' | 'default'> = {
                              POLICY: 'primary',
                              GEO: 'secondary',
                              COMMODITY: 'warning',
                              SUPPLIER: 'info',
                              CUSTOMER: 'success',
                              AGENCY: 'error',
                              SITE: 'info',
                              CURRENCY: 'warning',
                            };
                            const chipColor = colorMap[prefix] || 'default';
                            return (
                              <Chip
                                key={`${key}-${idx}`}
                                label={value || key}
                                size="small"
                                color={chipColor}
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              />
                            );
                          })}
                        </Stack>
                      )}

                      {/* Evidence */}
                      {detail.extraction.evidence && detail.extraction.evidence.length > 0 && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Evidence:
                          </Typography>
                          {detail.extraction.evidence.map((ev, idx) => (
                            <Typography
                              key={idx}
                              variant="caption"
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                bgcolor: 'action.hover',
                                borderRadius: 0.5,
                                fontFamily: 'monospace',
                              }}
                            >
                              {ev}
                            </Typography>
                          ))}
                        </Stack>
                      )}

                      {/* Summary */}
                      {detail.extraction.summary && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                          "{detail.extraction.summary}"
                        </Typography>
                      )}

                      {/* Extracted At */}
                      {detail.extraction.extractedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Extracted: {new Date(detail.extraction.extractedAt).toLocaleString()}
                        </Typography>
                      )}
                    </Stack>

                    <Divider />
                  </>
                )}

                {/* Routing Quality Analysis */}
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1">Routing Quality</Typography>
                    {detail.targetedInstruments.length > 0 && (
                      <Chip
                        label={`${detail.storylineInstruments.length} / ${detail.targetedInstruments.length} accepted (${Math.round((detail.storylineInstruments.length / detail.targetedInstruments.length) * 100)}%)`}
                        size="small"
                        color={
                          detail.storylineInstruments.length / detail.targetedInstruments.length >= 0.4
                            ? 'success'
                            : detail.storylineInstruments.length / detail.targetedInstruments.length >= 0.2
                            ? 'warning'
                            : 'error'
                        }
                      />
                    )}
                  </Stack>

                  {/* Accepted (Created Storylines) */}
                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      ✓ Accepted ({detail.storylineInstruments.length})
                    </Typography>
                    {detail.storylineInstruments.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        No storylines created yet.
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, pl: 2 }}>
                        {detail.storylineInstruments.map((instrumentId) => (
                          <Chip
                            key={instrumentId}
                            label={instrumentId.replace('instrument:', '')}
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        ))}
                      </Stack>
                    )}
                  </Stack>

                  {/* Rejected (Targeted but not created) */}
                  {(() => {
                    const rejected = detail.targetedInstruments.filter(
                      (id) => !detail.storylineInstruments.includes(id)
                    );
                    return rejected.length > 0 ? (
                      <Stack spacing={1}>
                        <Typography variant="body2" fontWeight={600} color="error.main">
                          ✗ Rejected ({rejected.length})
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1, pl: 2 }}>
                          {rejected.map((instrumentId) => (
                            <Chip
                              key={instrumentId}
                              label={instrumentId.replace('instrument:', '')}
                              size="small"
                              color="default"
                              variant="outlined"
                              sx={{ textDecoration: 'line-through', opacity: 0.6 }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    ) : null;
                  })()}

                  {/* Pending (Targeted but not yet processed) */}
                  {(() => {
                    const pending = detail.targetedInstruments.filter(
                      (id) => !detail.storylineInstruments.includes(id)
                    );
                    return pending.length > 0 && detail.storylineInstruments.length === 0 ? (
                      <Stack spacing={1}>
                        <Typography variant="body2" fontWeight={600} color="warning.main">
                          ⏳ Pending ({pending.length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                          Waiting for storyline workers to process...
                        </Typography>
                      </Stack>
                    ) : null;
                  })()}
                </Stack>

                <Divider />

                {/* Force Storyline Creation */}
                <Stack spacing={1.5}>
                  <Typography variant="subtitle1">Force Storyline Creation</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manually trigger storyline creation for a specific instrument from this news article.
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Select
                      value={selectedInstrumentId}
                      onChange={(e) => setSelectedInstrumentId(e.target.value)}
                      displayEmpty
                      size="small"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="" disabled>
                        Select instrument
                      </MenuItem>
                      {detail.targetedInstruments.map((instrumentId) => (
                        <MenuItem key={instrumentId} value={instrumentId}>
                          {instrumentId.replace('instrument:', '')}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="contained"
                      onClick={handleTrigger}
                      disabled={!selectedInstrumentId || triggerMutation.isPending}
                    >
                      {triggerMutation.isPending ? 'Triggering...' : 'Trigger'}
                    </Button>
                  </Stack>

                  {triggerMutation.isError && (
                    <Alert severity="error">
                      {triggerMutation.error instanceof Error
                        ? triggerMutation.error.message
                        : 'Failed to trigger storyline'}
                    </Alert>
                  )}

                  {triggerMutation.isSuccess && (
                    <Alert severity="success">Storyline creation triggered successfully!</Alert>
                  )}
                </Stack>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Paper>
    </Box>
  );
};

export default StewardDashboardPage;
